import type { ProposalAction } from '../../types/agent';
import { useEffect, useState } from 'react';

const REGION_LABELS: Record<string, string> = {
  head_neck: 'Head / Neck',
  left_shoulder: 'L Shoulder',
  right_shoulder: 'R Shoulder',
  upper_back: 'Upper Back',
  lower_back: 'Lower Back',
  left_hip: 'L Hip',
  right_hip: 'R Hip',
  left_quad: 'L Quad',
  right_quad: 'R Quad',
  left_hamstring: 'L Hamstring',
  right_hamstring: 'R Hamstring',
  left_knee: 'L Knee',
  right_knee: 'R Knee',
  left_ankle: 'L Ankle',
  right_ankle: 'R Ankle',
  left_calf: 'L Calf',
  right_calf: 'R Calf',
  other: 'Other',
};

const PAIN_TYPES = ['soreness', 'sharp', 'tightness', 'ache'] as const;
const SEVERITIES = ['minor', 'moderate', 'severe'] as const;

const ACTION_LABELS: Record<string, string> = {
  reply_sms: 'Reply to athlete',
  session_adjustment_note: 'Session adjustment note',
  staff_routing: 'Route to staff',
  injury_record_proposal: 'Log injury record',
};

function bucketSeverity(painLevel: number): string {
  if (painLevel <= 3) return 'minor';
  if (painLevel <= 6) return 'moderate';
  return 'severe';
}

// ---------------------------------------------------------------------------
// Per-action editors
// ---------------------------------------------------------------------------

function ReplySmsEditor({
  payload, onChange, disabled,
}: { payload: any; onChange: (p: any) => void; disabled: boolean }) {
  const text = payload?.text ?? '';
  const chars = text.length;
  return (
    <div className="space-y-1">
      <label className="text-xs text-neutral-600">SMS to athlete</label>
      <textarea
        className="w-full text-sm p-2 border border-neutral-300 disabled:bg-neutral-50"
        rows={4}
        value={text}
        disabled={disabled}
        onChange={(e) => onChange({ ...payload, text: e.target.value })}
      />
      <div className={`text-xs ${chars > 160 ? 'text-amber-600' : 'text-neutral-400'}`}>
        {chars} chars{chars > 160 ? ' (may split across SMS segments)' : ''}
      </div>
    </div>
  );
}

function SessionNoteEditor({
  payload, onChange, disabled,
}: { payload: any; onChange: (p: any) => void; disabled: boolean }) {
  const today = new Date().toISOString().split('T')[0];
  return (
    <div className="space-y-2">
      <div className="flex gap-2 items-end">
        <div className="flex-1 space-y-1">
          <label className="text-xs text-neutral-600">Applies to session date</label>
          <input
            type="date"
            className="w-full text-sm p-2 border border-neutral-300 disabled:bg-neutral-50"
            value={payload?.date ?? today}
            disabled={disabled}
            onChange={(e) => onChange({ ...payload, date: e.target.value })}
          />
        </div>
      </div>
      <div className="space-y-1">
        <label className="text-xs text-neutral-600">Adjustment narrative</label>
        <textarea
          className="w-full text-sm p-2 border border-neutral-300 disabled:bg-neutral-50"
          rows={4}
          value={payload?.narrative ?? ''}
          disabled={disabled}
          onChange={(e) => onChange({ ...payload, narrative: e.target.value })}
        />
      </div>
    </div>
  );
}

function StaffRoutingEditor({
  payload, onChange, disabled,
}: { payload: any; onChange: (p: any) => void; disabled: boolean }) {
  return (
    <div className="space-y-2">
      <div className="space-y-1">
        <label className="text-xs text-neutral-600">Route to</label>
        <input
          type="text"
          className="w-full text-sm p-2 border border-neutral-300 disabled:bg-neutral-50"
          value={payload?.to_user_hint ?? ''}
          placeholder="e.g. athletic_trainer"
          disabled={disabled}
          onChange={(e) => onChange({ ...payload, to_user_hint: e.target.value })}
        />
        <div className="text-xs text-neutral-400">
          Sprint 1: routes to the team's secondary coach regardless of hint.
        </div>
      </div>
      <div className="space-y-1">
        <label className="text-xs text-neutral-600">Reason</label>
        <textarea
          className="w-full text-sm p-2 border border-neutral-300 disabled:bg-neutral-50"
          rows={3}
          value={payload?.reason ?? ''}
          disabled={disabled}
          onChange={(e) => onChange({ ...payload, reason: e.target.value })}
        />
      </div>
    </div>
  );
}

function InjuryRecordEditor({
  payload, onChange, disabled,
}: { payload: any; onChange: (p: any) => void; disabled: boolean }) {
  const today = new Date().toISOString().split('T')[0];
  const painLevel = Number(payload?.pain_level ?? 0);

  const handlePain = (next: number) => {
    onChange({
      ...payload,
      pain_level: next,
      severity: bucketSeverity(next),
    });
  };

  return (
    <div className="space-y-3">
      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <label className="text-xs text-neutral-600">Body region</label>
          <select
            className="w-full text-sm p-2 border border-neutral-300 disabled:bg-neutral-50"
            value={payload?.body_region ?? 'right_hamstring'}
            disabled={disabled}
            onChange={(e) => onChange({ ...payload, body_region: e.target.value })}
          >
            {Object.entries(REGION_LABELS).map(([v, l]) => (
              <option key={v} value={v}>{l}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1">
          <label className="text-xs text-neutral-600">Pain type</label>
          <select
            className="w-full text-sm p-2 border border-neutral-300 disabled:bg-neutral-50"
            value={payload?.pain_type ?? 'tightness'}
            disabled={disabled}
            onChange={(e) => onChange({ ...payload, pain_type: e.target.value })}
          >
            {PAIN_TYPES.map(p => (
              <option key={p} value={p}>{p}</option>
            ))}
          </select>
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-xs text-neutral-600">
          Pain level <span className="text-neutral-400">({painLevel || '—'}/10)</span>
        </label>
        <div className="flex items-center gap-2">
          <input
            type="range"
            min={1}
            max={10}
            step={1}
            value={painLevel || 1}
            disabled={disabled}
            onChange={(e) => handlePain(Number(e.target.value))}
            className="flex-1"
          />
          <span className="text-xs px-2 py-0.5 bg-neutral-200 rounded w-20 text-center">
            {payload?.severity ?? bucketSeverity(painLevel || 1)}
          </span>
        </div>
        <div className="text-xs text-neutral-400">
          Severity auto-bucketed: 1-3 minor · 4-6 moderate · 7-10 severe
        </div>
      </div>

      <div className="grid grid-cols-2 gap-2">
        <div className="space-y-1">
          <label className="text-xs text-neutral-600">Injury date</label>
          <input
            type="date"
            className="w-full text-sm p-2 border border-neutral-300 disabled:bg-neutral-50"
            value={payload?.injury_date ?? today}
            disabled={disabled}
            onChange={(e) => onChange({ ...payload, injury_date: e.target.value })}
          />
        </div>
        <div className="space-y-1 flex flex-col">
          <label className="text-xs text-neutral-600">Onset</label>
          <label className="flex items-center gap-2 text-sm p-2 border border-neutral-300">
            <input
              type="checkbox"
              checked={!!payload?.is_new_onset}
              disabled={disabled}
              onChange={(e) => onChange({ ...payload, is_new_onset: e.target.checked })}
            />
            New onset
          </label>
        </div>
      </div>

      <div className="space-y-1">
        <label className="text-xs text-neutral-600">Notes</label>
        <textarea
          className="w-full text-sm p-2 border border-neutral-300 disabled:bg-neutral-50"
          rows={3}
          value={payload?.notes ?? ''}
          disabled={disabled}
          onChange={(e) => onChange({ ...payload, notes: e.target.value })}
        />
      </div>

      <div className="text-xs text-amber-700 bg-amber-50 border border-amber-200 p-2">
        Two-step approval — primary coach approves here, then the team's athletic
        trainer (or secondary coach) confirms before an InjuryRecord is created.
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Card
// ---------------------------------------------------------------------------

export function ActionCard({
  action, onToggle, selected, onEdit,
}: {
  action: ProposalAction;
  selected: boolean;
  onToggle: (id: string, next: boolean) => void;
  onEdit: (id: string, patch: Record<string, any>) => void;
}) {
  const [local, setLocal] = useState<Record<string, any>>(action.payload ?? {});
  const disabled = action.status !== 'pending';

  const handleChange = (next: Record<string, any>) => {
    setLocal(next);
    onEdit(action.id, next);
  };

  // Backfill required date fields the LLM tool sometimes omits. The display
  // inputs already render `today` as the fallback `value`, but `onChange`
  // only fires on user keystroke — so a coach who clicks Approve without
  // touching the date sends a payload missing `date`/`injury_date` and the
  // executor fails (e.g. SessionAdjustmentNoteExecutor calls
  // date.fromisoformat(payload["date"])). Seed once on mount when pending.
  useEffect(() => {
    if (action.status !== 'pending') return;
    const today = new Date().toISOString().split('T')[0];
    const patch: Record<string, any> = {};
    if (action.action_type === 'session_adjustment_note' && !local.date) {
      patch.date = today;
    }
    if (action.action_type === 'injury_record_proposal' && !local.injury_date) {
      patch.injury_date = today;
    }
    if (Object.keys(patch).length > 0) {
      handleChange({ ...local, ...patch });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const label = ACTION_LABELS[action.action_type] ?? action.action_type;
  const executionResult = (action as any).execution_result;
  const errorMessage =
    typeof executionResult === 'object' && executionResult !== null
      ? executionResult.error ?? executionResult.message ?? null
      : null;

  return (
    <div className="border border-neutral-300 p-3 space-y-2">
      <header className="flex items-center justify-between">
        <label className="flex items-center gap-2">
          <input
            type="checkbox"
            checked={selected}
            disabled={disabled}
            onChange={(e) => onToggle(action.id, e.target.checked)}
          />
          <strong>{label}</strong>
          <span
            className={`text-xs ${
              action.status === 'executed'
                ? 'text-emerald-700 dark:text-emerald-400'
                : action.status === 'failed'
                  ? 'text-red-700 dark:text-red-400'
                  : 'text-neutral-500 dark:text-neutral-400'
            }`}
          >
            [{action.status}]
          </span>
        </label>
      </header>

      {action.status === 'failed' && (
        <div className="text-xs text-red-700 dark:text-red-400 border border-red-200 dark:border-red-900/50 bg-red-50 dark:bg-red-950/30 p-2">
          Execution failed{errorMessage ? `: ${errorMessage}` : ' — check backend logs.'}
        </div>
      )}
      {action.status === 'executed' && (
        <div className="text-xs text-emerald-700 dark:text-emerald-400">
          Executed{action.executed_at ? ` ${new Date(action.executed_at).toLocaleString()}` : ''}
        </div>
      )}

      {action.action_type === 'reply_sms' && (
        <ReplySmsEditor payload={local} onChange={handleChange} disabled={disabled} />
      )}
      {action.action_type === 'session_adjustment_note' && (
        <SessionNoteEditor payload={local} onChange={handleChange} disabled={disabled} />
      )}
      {action.action_type === 'staff_routing' && (
        <StaffRoutingEditor payload={local} onChange={handleChange} disabled={disabled} />
      )}
      {action.action_type === 'injury_record_proposal' && (
        <InjuryRecordEditor payload={local} onChange={handleChange} disabled={disabled} />
      )}
    </div>
  );
}
